import { createPagePagingActions, SortDirection, Table, TableQuery, useTableQuery, useTableQueryPaging, useTableQuerySort } from "@comet/admin";
import { ContentWrapper, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { type GQLImagesAdminQuery, type GQLImagesAdminQueryVariables } from "@src/graphql.generated";
import { type FC, type PropsWithChildren, useCallback, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { imagesAdminQuery } from "./ImageLog.gql";

export const ImageLogTable: FC<PropsWithChildren<{ cameraId: string; isRefetching: boolean }>> = ({ cameraId, isRefetching }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "mailedAt", direction: SortDirection.DESC });
    const size = 5;
    const addAdditionalQueryVariablesFn = useCallback(() => ({ cameraId, size }), [cameraId, size]);
    const memoizedVariables = useMemoizedTableVariables<GQLImagesAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api, refetch } = useTableQuery<GQLImagesAdminQuery, GQLImagesAdminQueryVariables>()(imagesAdminQuery, {
        resolveTableData: ({ imagesAdmin }) => ({
            data: imagesAdmin.nodes,
            totalCount: imagesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, imagesAdmin),
        }),
        variables: memoizedVariables,
    });

    useEffect(() => {
        refetch();
    }, [refetch, isRefetching]);

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.camera.images", defaultMessage: "Bilder" })}>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <Table
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.camera.images.count",
                                defaultMessage: "{count, plural, one {Bild} other {Bilder}} empfangen",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "camera.imageTaken",
                                sortable: false,
                                header: <FormattedMessage id="reviermanager.camera.imageTaken" defaultMessage="Aktivität" />,
                                render: () => <FormattedMessage id="reviermanager.camera.imageReceived" defaultMessage="Bild empfangen" />,
                            },
                            {
                                name: "image.mailedAt",
                                sortable: true,
                                header: <FormattedMessage id="reviermanager.camera.image.receivedAt" defaultMessage="Datum" />,
                                render: ({ mailedAt }) => <FormattedDateWidthDateTime value={mailedAt} />,
                            },
                        ]}
                    />
                )}
            </TableQuery>
        </ContentWrapper>
    );
};
